// components/Sidebar.js
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchWithAuth } from '../auth';

export default function Sidebar({ onNavigate, isOpen }) {
  const location = useLocation();
  const [nodes, setNodes] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());

  const k = (...parts) => parts.join('::');

  // Load root tractates on mount
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth('/api/tractates/');
        const list = res.ok ? await res.json() : [];
        if (ignore) return;
        setNodes(
          list
            .filter(t => t.name !== 'מיקוד מלחמה תשפו ולמידה בשעת חירום')
            .map(t => ({
              type: 'tractate',
              key: k('t', t.id),
              title: t.name,
              tractateId: t.id,
              toshbaVisible: t.toshba_visible !== false,
              talmudVisible: t.talmud_visible !== false,
              children: null,
            }))
        );
      } catch {}
    })();
    return () => { ignore = true; };
  }, []);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setExpanded(new Set());
    setNodes(prev => prev.map(n => ({ ...n, children: null })));
  }, [isOpen]);

  const tail = (domain, chapter) =>
    `?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;

  function updateNode(list, key, updater) {
    return list.map(n =>
      n.key === key
        ? updater(n)
        : n.children
          ? { ...n, children: updateNode(n.children, key, updater) }
          : n
    );
  }

  function getTractateTitle(tractateId) {
    const node = nodes.find(n => n.type === 'tractate' && n.tractateId === tractateId);
    return node?.title || '';
  }

  async function ensureChildren(node) {
    if (node.children?.length) return;

    if (node.type === 'tractate') {
      const id = node.tractateId;
      const kids = [];
      if (node.toshbaVisible !== false) {
        kids.push({ type: 'domain', key: k('t', id, 'dom', 'toshba'), title: 'מסלול תושב״ע', tractateId: id, domain: 'toshba', children: null });
      }
      if (node.talmudVisible !== false) {
        kids.push({ type: 'domain', key: k('t', id, 'dom', 'talmud'), title: 'מסלול תלמוד',  tractateId: id, domain: 'talmud', children: null });
      }
      setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
      return;
    }

    if (node.type === 'domain') {
      const id = node.tractateId;
      const currentChapter = new URLSearchParams(location.search).get('chapter') || '';
      const kids = [
        { type: 'link', key: k('t', id, 'dom', node.domain, 'year'),
          title: 'תוכנית שנתית',
          href: `/tractates/${id}/year-plan${tail(node.domain, currentChapter)}` },
      ];
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/chapters/?domain=${node.domain}`);
        const chs = res.ok ? await res.json() : [];
        chs.forEach(c => {
          const title = c.display?.trim() || c.title?.trim() || (c.number != null ? `פרק ${c.number}` : 'פרק');
          kids.push({ type: 'chapter', key: k('t', id, 'dom', node.domain, 'ch', c.id),
            title, tractateId: id, domain: node.domain, chapterId: c.id, children: null });
        });
      } catch {}
      setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
      return;
    }

    if (node.type === 'chapter') {
      try {
        const qs = new URLSearchParams({ domain: node.domain, chapter: String(node.chapterId) });
        const res = await fetchWithAuth(`/api/tractates/${node.tractateId}/topics/?${qs}`);
        const topics = res.ok ? await res.json() : [];
        const kids = topics.map(tp => ({
          type: 'topic',
          key: k('t', node.tractateId, 'dom', node.domain, 'ch', node.chapterId, 'tp', tp.id),
          title: tp.name,
          tractateId: node.tractateId,
          domain: node.domain,
          chapterId: node.chapterId,
          topicId: tp.id,
          children: null,
        }));
        setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
      } catch {}
      return;
    }

    if (node.type === 'topic') {
      const base = `/tractates/${node.tractateId}/topics/${node.topicId}`;
      const tractateName = getTractateTitle(node.tractateId);
      const cats = [
        { title: 'הכנת הסוגיה',  slug: 'preparation' },
        { title: 'כלים להוראה',  slug: 'tools' },
        { title: 'הערכה ומשחוק', slug: 'evaluation' },
        { title: 'אמצעים חזותיים - תרשימים, תמונות וסרטונים', slug: 'visual' },
      ];
      if (node.domain === 'talmud') {
        cats.push({ title: 'תוספות וסוגיות בעיון', slug: 'tosafot' });
      }
      const kids = cats.map(c => ({
        type: 'link',
        key: k('t', node.tractateId, 'dom', node.domain, 'ch', node.chapterId, 'tp', node.topicId, 'cat', c.slug),
        title: c.title,
        href: `${base}/${c.slug}${tail(node.domain, node.chapterId)}`,
        state: { tractateName, topicName: node.title },
      }));
      setNodes(prev => updateNode(prev, node.key, n => ({ ...n, children: kids })));
    }
  }

  function onToggle(node, open) {
    setExpanded(prev => {
      const next = new Set(prev);
      open ? next.add(node.key) : next.delete(node.key);
      return next;
    });
    if (open) ensureChildren(node);
  }

  const hasExpanded = expanded.size > 0;

  return (
    <nav role="navigation" aria-label="ניווט מסכתות">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-black/5 font-bold text-secondary-900 dark:text-white">
        <span>ניווט</span>
        <button
          type="button"
          className="bg-transparent text-secondary-600 dark:text-secondary-300
                     border border-secondary-300 dark:border-secondary-600
                     rounded-xl px-2.5 py-1 text-sm cursor-pointer transition-colors
                     hover:bg-primary-50 dark:hover:bg-secondary-800"
          onClick={() => setExpanded(new Set())}
          aria-label="כווץ"
          title="כווץ"
          style={{ visibility: hasExpanded ? 'visible' : 'hidden', pointerEvents: hasExpanded ? 'auto' : 'none' }}
        >
          כווץ
        </button>
      </div>

      {/* Tree */}
      <div className="px-3 pt-4 pb-5">
        <Tree nodes={nodes} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
      </div>
    </nav>
  );
}

function Tree({ nodes, expanded, onToggle, onNavigate }) {
  return (
    <div>
      {nodes.map(n => (
        <TreeNode key={n.key} node={n} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function TreeNode({ node, expanded, onToggle, onNavigate }) {
  const isOpen = expanded.has(node.key);

  // Leaf link
  if (node.type === 'link') {
    return (
      <div>
        <Link
          to={node.href}
          state={node.state}
          onClick={onNavigate}
          className="block px-2 py-1.5 rounded-lg text-secondary-800 dark:text-secondary-200
                     text-sm no-underline hover:bg-primary-600/[.08] hover:no-underline
                     transition-colors duration-150"
        >
          {node.title}
        </Link>
      </div>
    );
  }

  // Expandable node
  return (
    <details
      className="my-0.5"
      open={isOpen}
      onToggle={(e) => onToggle(node, e.currentTarget.open)}
    >
      <summary
        className="list-none flex items-center px-1 py-2 rounded-lg
                   text-secondary-800 dark:text-secondary-200
                   cursor-pointer select-none
                   hover:bg-primary-600/[.08] transition-colors duration-150"
      >
        {node.href
          ? <Link to={node.href} onClick={onNavigate} className="no-underline hover:no-underline">{node.title}</Link>
          : <span>{node.title}</span>
        }
      </summary>

      {node.children?.length > 0 && (
        <div className="ps-3 border-s-2 border-secondary-200 dark:border-secondary-700 ms-2">
          {node.children.map(ch => (
            <TreeNode key={ch.key} node={ch} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </details>
  );
}
